import { Component, OnInit, Input, ViewChild, Output, EventEmitter, ElementRef, OnChanges } from '@angular/core';
import { Message } from '../../case/model/message';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MessageService } from '../../core/service/message.service';
import { NGXLogger } from 'ngx-logger';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { User } from 'src/app/user/model/user';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { TranslateService } from '@ngx-translate/core';
import { IntlService } from '@progress/kendo-angular-intl';
import { SettingService } from 'src/app/core/service/setting.service';
import { UserService } from 'src/app/core/service/user.service';
import { UploadEvent } from '@progress/kendo-angular-upload';
import { CustomFile } from '../model/custom-file';
import { DocumentService } from 'src/app/core/service/document.service';
import { sortBy } from 'lodash';

@Component({
  standalone: false,
  selector: 'app-message-reply',
  templateUrl: './message-reply.component.html',
  styleUrls: ['./message-reply.component.scss']
})
export class MessageReplyComponent implements OnInit, OnChanges
{
  @ViewChild('textBodyControl') public textBodyControl: any;

  @Input() public messageReplyOpened = false;
  @Input() public messageToReply: Message;
  @Input() public messageList: any;

  @Output() public saving: EventEmitter<any> = new EventEmitter();

  public loaded = false;
  public isAll = false;
  public message: Message = new Message();
  public errorMessage: string;
  public currentUser: User;
  public autoMessage = false;
  public onText = '';
  public wroteText = '';
  public reText = '';
  public savingMessage = false;
  public attachmentsEnabled = false;
  public selectedFiles: CustomFile[] = [];
  public communityEnabled = false;
  public searchTerm: string;
  public toUsersFiltered: User[] = [];
  public replyTo: User[] = [];

  /**
   * Message form.
   */
  public messageForm: any;
  private toUsers: User[] = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private messageService: MessageService,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private translate: TranslateService,
    private authService: AuthenticationService,
    private intlService: IntlService,
    private settingService: SettingService,
    private elem: ElementRef,
    private documentService: DocumentService,
    private userService: UserService)
  {
    this.translate.get(['ON', 'WROTE', 'RE']).subscribe({
      next: (value) =>
      {
        this.onText = value.ON;
        this.wroteText = value.WROTE;
        this.reText = value.RE;
      }
    });
  }

  /**
   * Body form control
   *
   */
  public get body(): UntypedFormControl
  {
    return this.messageForm.get('body');
  }

  @Input() public set all(value: boolean)
  {
    this.isAll = value;
    this.loaded = false;
    this.buildForm();
  }

  public ngOnChanges(changes: any): void
  {
    if (changes.messageReplyOpened?.currentValue === true)
    {
      this.savingMessage = false;
      this.saving.emit(false);
    }
  }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.communityEnabled = sessionSetting.communityEnabled;
        this.attachmentsEnabled = (sessionSetting.attachmentsRoles !== '') ?
          this.userService.isRoleInList(this.authService.currentUser.role, sessionSetting.attachmentsRoles) : false;
      }
    });
  }

  /**
   * Form submission event handler.
   */
  public onSubmit(): void
  {
    this.savingMessage = true;
    this.saving.emit(true);

    this.message = this.messageForm.value;
    this.message.fromId = this.currentUser.id;
    this.message.from = this.currentUser.name;
    this.message.fromUsername = this.currentUser.userName;
    this.message.subject = (this.messageToReply.subject.substring(0, 2) !== this.reText) ?
      this.reText + ': ' + this.messageToReply.subject : this.messageToReply.subject;
    this.message.caseId = this.messageToReply.caseId;
    this.message.memberId = this.messageToReply.memberId;
    this.message.files = this.selectedFiles;
    this.message.to = this.messageForm.get('to').value;
    const originalBody = this.messageForm.get('body').value.replace(/<br\/>/g, '\r');
    this.message.body = this.messageForm.get('replyBody').value + '\r\r' + originalBody;

    this.message.to.forEach(to =>
    {
      if (this.currentUser.id === to.id)
      {
        this.autoMessage = true;
      }
    });

    this.messageService.saveMessage(this.message).subscribe({
      next: (data) =>
      {
        this.message = data;
        if (this.autoMessage)
        {
          if (data.body.indexOf('\n\n\n' + this.onText) > -1)
          {
            data.body = data.body.substring(0, data.body.indexOf('\n\n\n' + this.onText));
          }
          if (this.messageList)
          {
            if (this.messageList.data) { this.messageList.data.unshift(data); }
            if (this.messageList.gridData.data) { this.messageList.gridData.data.unshift(data); }
          }
        }
        this.messageHandlerService.displaySuccess('SEND-SUCCESSFULLY');
        this.savingMessage = false;
        this.saving.emit(false);
      },
      error: error =>
      {
        this.logger.error(error);

        // Error occurred during save, reset the model and display error message
        this.errorMessage = error.message;
        this.message = new Message();
        this.buildForm();
        this.selectedFiles = [];
        this.savingMessage = false;
        this.saving.emit(false);
      },
      complete: () =>
      {
        this.messageReplyOpened = false;
      }
    });
  }

  /**
   * Check if user from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.enabled && !itemArgs.dataItem.inRegistration;
  }

  public filterToUsers(filterValue: any): void
  {
    this.searchTerm = filterValue;
    this.toUsersFiltered = this.toUsers
      .filter(user => user.name.toLowerCase().includes(filterValue.toLowerCase()));
  }

  public selectAttachment(): void 
  {
    const uploadButton = this.elem.nativeElement.querySelectorAll('input[name="files"]');
    uploadButton[0].click();
  }

  /**
   * File has been selected.
   *
   * @param event Event
   */
  public onFileChanged(event: UploadEvent): void
  {
    event.files.forEach((file) =>
    {
      if (this.selectedFiles.find(f => f.name === file.name) !== undefined)
      {
        this.messageHandlerService.displayError('FILE-ALREADY-ATTACHED');
        return;
      }
      this.messageForm.markAsDirty();
      const customFile = new CustomFile();
      customFile.name = file.name;
      customFile.type = file.rawFile.type;
      this.setFileFromBlob(customFile, file.rawFile);
      this.selectedFiles.push(customFile);
    });
  }

  /**
   * Return the proper class to used based on the file mime type.
   *
   * @param mimeType Mime Type of the file.
   * @returns String
   */
  public getFileTypeClass(mimeType: string): string
  {
    return this.documentService.getFileTypeClass(mimeType);
  }

  public deleteAttachment(name: string): void
  {
    this.selectedFiles = this.selectedFiles.filter(f => f.name !== name);
  }

  public send(): void
  {
    this.onSubmit();
  }

  public cancel(): void
  {
    this.messageReplyOpened = false;
  }

  public formValid(): boolean
  {
    return this.messageForm !== undefined && this.messageForm.valid;
  }

  /**
   * Set the selected file.
   *
   * @param file Selected file.
   */
  private setFileFromBlob(customFile: CustomFile, file: Blob): void
  {
    const reader = new FileReader();
    reader.addEventListener('load', () =>
    {
      const base64: any = reader.result;
      customFile.data = base64.split('base64,')[1];
    }, false);
    reader.readAsDataURL(file);
  }

  /**
   * Set the focus to the textarea control
   */
  private setFocus(): void
  {
    setTimeout(() =>
    {
      if (this.textBodyControl !== undefined)
      {
        this.textBodyControl.nativeElement.focus();
        this.textBodyControl.nativeElement.selectionStart = 0;
        this.textBodyControl.nativeElement.selectionEnd = 0;
      }
    });
  }

  private buildForm(): void
  {
    setTimeout(() =>
    {
      if (!this.loaded)
      {
        this.userService.getCachedUsers(false, this.communityEnabled, this.messageToReply.memberId, false, 'MessagesRoles').subscribe({
          next: (data) =>
          {
            this.toUsers = sortBy(data, ['name']);
            this.filterToUsers('');

            this.currentUser = this.authService.currentUser;

            // Copy from user
            const users = [];
            const fromUser = this.toUsers.find(x => x.id === this.messageToReply.fromId);
            if (fromUser) { users.push(fromUser); }

            // Copy all to user(s)
            if (this.isAll && this.messageToReply.to && this.messageToReply.to.length > 0)
            {
              this.messageToReply.to.forEach((toUser: any) =>
              {
                toUser.id = parseInt(toUser.id, 10);
                if (users.find(x => x.id === toUser.id) === undefined) { users.push(toUser); }
              });
            }
            this.replyTo = users;

            const user = new User();
            user.id = this.messageToReply.fromId;
            user.name = this.messageToReply.from;
            user.userName = this.messageToReply.fromUsername;

            const sentOn = this.intlService.parseDate(this.messageToReply.sentOn.toLocaleString());
            const messageBody = this.messageToReply.body.replace(/[\n\r]/g, '<br/>');
            this.messageForm = this.formBuilder.group({
              to: [{ value: this.replyTo, disabled: this.savingMessage }, Validators.required],
              replyBody: [null, Validators.required],
              body: this.onText + ' ' + this.intlService.formatDate(sentOn, 'g') + ' ' + this.messageToReply.from +
                ' ' + this.wroteText + ': <br/>' + messageBody
            });
            this.loaded = true;
            this.setFocus();
          }
        });
      }
    });
  }
}
